import * as React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { PAType, PAMode } from "../../ProfitAnalysisPage";

export interface MobilePaTypeViewProps {
  paType: PAType;
  mode: PAMode;
}

/**
 * PA Type tab on mobile.
 *
 * Per business rule: a job has exactly one active PA (spec or sold).  Conversion
 * between types is handled in the backend, so there's nothing to switch here —
 * the tab is purely informational.
 *
 *   Create mode: shows the type the user picked when starting the workflow.
 *   Edit / view mode: shows the saved PA type.
 *
 * No "other PAs for this job" list (would require a new API call) and no
 * switcher (would create two active PAs for one job).
 */
export function MobilePaTypeView({ paType, mode }: MobilePaTypeViewProps) {
  const isSpec = paType === "spec";
  const label = isSpec ? "Spec Job" : "Sold Job";
  const icon = isSpec ? <HomeIcon fontSize="large" /> : <ShoppingCartIcon fontSize="large" />;

  const description = isSpec
    ? "A spec home is built without a specific buyer. The PA tracks projected profit against an exit-strategy target."
    : "A sold home has a signed contract with a buyer. The PA tracks actual profit on the contracted sale.";

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        PA Type
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          p: 2.5,
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color: "#0a2540" }}>{icon}</Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Active type
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {label}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "rgba(183, 176, 255, 0.08)",
          p: 1.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          A job has one active profit analysis. Conversion between Spec and Sold
          is handled automatically when the job status changes
          {mode === "create" ? " — finish creating this PA first" : ""}.
        </Typography>
      </Paper>
    </Box>
  );
}
